import { Separator } from "@monbits/ui";
import { Copy, ExternalLink } from "lucide-react";
import type * as React from "react";
import PaymentStatusLabel from "src/components/payment-status/payment-status";
import SpinWithMask from "src/components/spin-with-mask/spin-with-mask";
import { StatusBadge } from "src/components/status-badge";
import TimeLabel from "src/components/time-label/time-label";
import { CURRENCY_SYMBOL, type Payment } from "src/types";
import copyToClipboard from "src/utils/copy-to-clipboard";

interface Props {
	data?: Payment;
	openNotificationFunc: (title: string, description: string) => void;
}

const displayPrice = (record: Payment) => {
	let ticker = `${record.currency} `;
	if (
		record.currency in CURRENCY_SYMBOL &&
		CURRENCY_SYMBOL[record.currency] !== ""
	) {
		ticker = CURRENCY_SYMBOL[record.currency];
	}

	return ticker + record.price;
};

const PaymentDescCard: React.FC<Props> = ({ data, openNotificationFunc }) => {
	const copyText = (text: string) => {
		copyToClipboard(text, openNotificationFunc);
	};

	if (!data) {
		return <SpinWithMask isLoading />;
	}

	const customerEmail = data.additionalInfo?.payment?.customerEmail;
	const selectedCurrency = data.additionalInfo?.payment?.selectedCurrency;
	const serviceFee = data.additionalInfo?.payment?.serviceFee;

	return (
		<div className="space-y-8">
			{/* Hero: price + status inline. Status is the merchant's
			    "what happened with this payment" answer. */}
			<div className="space-y-1.5">
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="flex flex-wrap items-center gap-2">
						<p className="text-xs text-muted-foreground">Price</p>
						<PaymentStatusLabel status={data.status} />
						{data.isTest && <StatusBadge type="warning">TEST</StatusBadge>}
					</div>
				</div>
				<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
					<span className="text-3xl font-semibold leading-none tracking-tight tabular-nums font-mono">
						{displayPrice(data)}
					</span>
					{data.currency && (
						<span className="text-lg font-medium text-muted-foreground">
							{data.currency}
						</span>
					)}
				</div>
				{serviceFee && (
					<p className="text-xs text-muted-foreground tabular-nums">
						Service fee:{" "}
						<span className="font-medium text-foreground">{serviceFee}</span>
					</p>
				)}
			</div>

			<Separator />

			{/* Payment URL — the actionable hero. The merchant shares this
			    with the customer. Same "Send to" treatment as the order
			    address card so visual language is consistent. */}
			{data.paymentUrl && (
				<div className="flex gap-2">
					<button
						type="button"
						aria-label="Copy payment URL"
						className="group flex-1 rounded-lg border border-border bg-muted/60 p-3 text-left transition-colors hover:bg-muted"
						onClick={() => copyText(data.paymentUrl || "")}
					>
						<div className="space-y-1.5">
							<p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
								Share with customer
							</p>
							<div className="flex items-start gap-3">
								<span className="flex-1 text-sm font-mono break-all leading-relaxed">
									{data.paymentUrl}
								</span>
								<Copy className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
							</div>
						</div>
					</button>
					<a
						href={data.paymentUrl}
						target="_blank"
						rel="noopener noreferrer"
						aria-label="Open payment URL in a new tab"
						className="flex flex-shrink-0 items-center justify-center rounded-lg border border-border bg-muted/60 px-3 transition-colors hover:bg-muted"
					>
						<ExternalLink className="h-4 w-4 text-muted-foreground hover:text-primary" />
					</a>
				</div>
			)}

			{/* Customer block — shown when we have customer info. Tinted
			    card mirrors the description card's chrome. */}
			{(customerEmail || selectedCurrency) && (
				<div className="rounded-lg border border-border bg-muted/40 p-3">
					<p className="mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
						Customer
					</p>
					<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
						{customerEmail && (
							<>
								<dt className="text-muted-foreground">Email</dt>
								<dd className="min-w-0">
									<button
										type="button"
										aria-label="Copy customer email"
										className="group ml-auto flex items-center gap-2"
										onClick={() => copyText(customerEmail)}
									>
										<span className="truncate font-medium">
											{customerEmail}
										</span>
										<Copy className="h-3 w-3 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
									</button>
								</dd>
							</>
						)}
						{selectedCurrency && (
							<>
								<dt className="text-muted-foreground">Paid with</dt>
								<dd className="text-right font-medium">{selectedCurrency}</dd>
							</>
						)}
					</dl>
				</div>
			)}

			<Separator />

			{/* Metadata — tighter group of reference info. */}
			<dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 text-sm">
				<dt className="text-muted-foreground">Payment ID</dt>
				<dd className="min-w-0">
					<button
						type="button"
						aria-label="Copy payment ID"
						className="group ml-auto flex items-center gap-2"
						onClick={() => copyText(data.id)}
					>
						<span className="truncate font-mono text-xs">{data.id}</span>
						<Copy className="h-3 w-3 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
					</button>
				</dd>

				{data.orderId && (
					<>
						<dt className="text-muted-foreground">Order ID</dt>
						<dd className="min-w-0">
							<button
								type="button"
								aria-label="Copy order ID"
								className="group ml-auto flex items-center gap-2"
								onClick={() => copyText(data.orderId || "")}
							>
								<span className="truncate font-medium">{data.orderId}</span>
								<Copy className="h-3 w-3 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
							</button>
						</dd>
					</>
				)}

				<dt className="text-muted-foreground">Created</dt>
				<dd className="text-right">
					<TimeLabel time={data.createdAt} />
				</dd>
			</dl>

			{/* Description — labeled card matching the address card chrome. */}
			{data.description && data.description !== "Not provided" && (
				<div className="rounded-lg border border-border bg-muted/40 p-3">
					<p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
						Description
					</p>
					<p className="text-sm leading-relaxed">{data.description}</p>
				</div>
			)}

			{/* Redirect URL — admin reference, only shown when configured. */}
			{data.redirectUrl && (
				<div className="rounded-lg border border-border bg-muted/40 p-3">
					<p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
						Redirect after payment
					</p>
					<p className="text-sm font-mono break-all leading-relaxed">
						{data.redirectUrl}
					</p>
				</div>
			)}
		</div>
	);
};

export default PaymentDescCard;
